import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, Input } from 'antd';
import type { InputRef } from 'antd';
import { DownOutlined, SearchOutlined, UpOutlined } from '@ant-design/icons';
import { saveAs } from 'file-saver';

import styles from './StructuredLogViewer.module.less';
import { useAppSelector } from '../../app/hooks';
import { selectLog } from '../tuzhi-vis/tuzhiVisSlice';
import {
  CONTINUATION_INDENT,
  LogDisplayRow,
  displayRowsToText,
  formatLevelCell,
  renderStructuredLogLines,
} from './structuredLogRender';

// 行高是常数，窗口化靠它算可视切片，所以行内不允许换行
const ROW_HEIGHT = 19;
// 上下各预渲染多少行。这是抗闪烁的主要旋钮，窗口边界离可视区越远，差的那一帧越看不见。
// 成本约每行 0.05ms，取 20 时约 50 行、单帧 2 到 3ms，余量还很大
const OVERSCAN_ROWS = 20;
// 贴底判定的容差，滚动条取整与亚像素会让精确相等不成立
const BOTTOM_TOLERANCE = 4;

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function levelClassName(level: string): string | undefined {
  if (level === 'ERROR' || level === 'CRITICAL') {
    return styles.levelError;
  }
  if (level === 'WARNING') {
    return styles.levelWarning;
  }
  return undefined;
}

interface StructuredLogViewerProps {
  projectId: number;
}

/**
 * 自研的固定行高窗口化列表。
 *
 * 注意，别换回现成的日志组件。调研过 @melloware/react-logviewer，它每滚动一帧都把全部行重解析一遍，
 * 数万行时单帧一百多毫秒，而能缓解的缓冲参数一个都没转发给底层。详见 design 的 D1。
 */
function StructuredLogViewer({ projectId }: StructuredLogViewerProps) {
  const lines = useAppSelector(state => selectLog(state).lines);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<InputRef | null>(null);

  // 展示行只在新增时追加，不每批整体重建，所以放在 ref 里配一个版本号触发重渲染
  const rowsRef = useRef<LogDisplayRow[]>([]);
  const flattenedCountRef = useRef(0);
  const [rowsVersion, setRowsVersion] = useState(0);

  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(0);
  const [following, setFollowing] = useState(true);

  const [keyword, setKeyword] = useState('');
  const [currentHit, setCurrentHit] = useState(0);

  useEffect(() => {
    if (lines.length < flattenedCountRef.current) {
      // 切项目时 clearLog 会把行数组归零，这里整体重建并回到贴底
      rowsRef.current = renderStructuredLogLines(lines);
      flattenedCountRef.current = lines.length;
      setFollowing(true);
      setScrollTop(0);
      setRowsVersion(version => version + 1);
      return;
    }
    if (lines.length === flattenedCountRef.current) {
      return;
    }
    // 只摊平新增的那一段。注意逐条 push 而不是展开传参，单批行数很大时展开会顶到参数个数上限
    const added = renderStructuredLogLines(lines.slice(flattenedCountRef.current));
    flattenedCountRef.current = lines.length;
    for (let i = 0; i < added.length; i += 1) {
      rowsRef.current.push(added[i]);
    }
    setRowsVersion(version => version + 1);
  }, [lines]);

  // 面板被拖拽改变尺寸不会触发 window resize，只能靠 ResizeObserver
  useEffect(() => {
    const element = scrollRef.current;
    if (!element) {
      return;
    }
    const measure = () => setViewportHeight(element.clientHeight);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const handleScroll = useCallback(() => {
    const element = scrollRef.current;
    if (!element) {
      return;
    }
    setScrollTop(element.scrollTop);
    setFollowing(element.scrollTop + element.clientHeight >= element.scrollHeight - BOTTOM_TOLERANCE);
  }, []);

  // 新行到达或面板变高时都要重新贴底，否则底部会露出一截空白
  useLayoutEffect(() => {
    const element = scrollRef.current;
    if (!element || !following) {
      return;
    }
    element.scrollTop = element.scrollHeight;
    setScrollTop(element.scrollTop);
  }, [rowsVersion, following, viewportHeight]);

  const totalRows = rowsRef.current.length;
  const totalHeight = totalRows * ROW_HEIGHT;
  // 行号栏宽度按最大位数留够，等宽字体下用 ch 换算，位数变化时不会跳动
  const gutterWidth = `${Math.max(4, String(Math.max(totalRows, 1)).length) + 1}ch`;
  const firstIndex = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN_ROWS);
  const visibleCount = Math.ceil(viewportHeight / ROW_HEIGHT) + OVERSCAN_ROWS * 2;
  const visibleRows = useMemo(
    () => rowsRef.current.slice(firstIndex, firstIndex + visibleCount),
    // rowsVersion 变化说明 rowsRef 内容已更新，靠它把 memo 关联到最新数据
    [firstIndex, visibleCount, rowsVersion],
  );

  const hits = useMemo(() => {
    const trimmed = keyword.trim();
    if (!trimmed) {
      return [] as number[];
    }
    // 用带 i 标志的正则做不区分大小写的匹配，避免逐行 toLowerCase 造出大量临时字符串。
    // 检索词里的元字符已转义，所以这里的表现仍是纯文本查找而不是正则
    const pattern = new RegExp(escapeRegExp(trimmed), 'i');
    const rows = rowsRef.current;
    const matchedIndexes: number[] = [];
    for (let i = 0; i < rows.length; i += 1) {
      const row = rows[i];
      const matched = row.kind === 'head'
        ? pattern.test(row.text) || pattern.test(row.ts) || pattern.test(row.instances) || pattern.test(row.level)
        : pattern.test(row.text);
      if (matched) {
        matchedIndexes.push(i);
      }
    }
    return matchedIndexes;
  }, [keyword, rowsVersion]);

  // 换词或日志清空后命中变少，把序号拉回范围内
  useEffect(() => {
    if (currentHit > hits.length - 1) {
      setCurrentHit(0);
    }
  }, [hits, currentHit]);

  const hitSet = useMemo(() => new Set(hits), [hits]);

  const goToHit = useCallback((index: number) => {
    const element = scrollRef.current;
    const targetRow = hits[index];
    if (!element || targetRow === undefined) {
      return;
    }
    setCurrentHit(index);
    element.scrollTop = Math.max(0, targetRow * ROW_HEIGHT - element.clientHeight / 2);
    setScrollTop(element.scrollTop);
  }, [hits]);

  // 只在面板容器上监听，焦点不在面板里时不拦截，浏览器原生查找照常
  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'f') {
      event.preventDefault();
      window.requestAnimationFrame(() => {
        searchInputRef.current?.focus();
        searchInputRef.current?.select();
      });
    }
  }, []);

  const handleKeywordChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setKeyword(event.target.value);
    setCurrentHit(0);
  }, []);

  const handleSearchKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter' || hits.length === 0) {
      return;
    }
    event.preventDefault();
    const step = event.shiftKey ? -1 : 1;
    goToHit((currentHit + step + hits.length) % hits.length);
  }, [currentHit, goToHit, hits.length]);

  const jumpToBottom = useCallback(() => {
    const element = scrollRef.current;
    setFollowing(true);
    if (element) {
      element.scrollTop = element.scrollHeight;
      setScrollTop(element.scrollTop);
    }
  }, []);

  const handleExport = useCallback(() => {
    const text = displayRowsToText(rowsRef.current);
    saveAs(new Blob([text], { type: 'text/plain;charset=utf-8' }), `project-${projectId}.log`);
  }, [projectId]);

  const hasKeyword = keyword.trim().length > 0;

  return (
    <div className={styles.root} onKeyDown={handleKeyDown}>
      <div className={styles.toolbar}>
        <Input
          ref={searchInputRef}
          size="small"
          allowClear
          className={styles.searchInput}
          placeholder="搜索日志（单行）"
          prefix={<SearchOutlined />}
          value={keyword}
          onChange={handleKeywordChange}
          onKeyDown={handleSearchKeyDown}
          aria-label="搜索日志"
        />
        <span className={styles.searchCount}>
          {hasKeyword ? (hits.length ? `${currentHit + 1} / ${hits.length}` : '0 处匹配') : ''}
        </span>
        <Button.Group>
          <Button
            size="small"
            icon={<UpOutlined />}
            aria-label="上一个匹配"
            disabled={hits.length === 0}
            onClick={() => goToHit((currentHit - 1 + hits.length) % hits.length)}
          />
          <Button
            size="small"
            icon={<DownOutlined />}
            aria-label="下一个匹配"
            disabled={hits.length === 0}
            onClick={() => goToHit((currentHit + 1) % hits.length)}
          />
          <Button size="small" onClick={jumpToBottom}>
            回到最新
          </Button>
          <Button size="small" onClick={handleExport}>
            下载 log
          </Button>
        </Button.Group>
      </div>
      <div className={styles.scroll} ref={scrollRef} onScroll={handleScroll} tabIndex={0} role="log">
        <div className={styles.canvas} style={{ height: totalHeight }}>
          <div style={{ transform: `translateY(${firstIndex * ROW_HEIGHT}px)` }}>
            {visibleRows.map((row, offset) => {
              const index = firstIndex + offset;
              const isCurrent = hasKeyword && hits[currentHit] === index;
              const isHit = hasKeyword && !isCurrent && hitSet.has(index);
              const classes = [styles.row];
              if (row.kind === 'continuation') {
                classes.push(styles.rowContinuation);
              }
              if (isCurrent) {
                classes.push(styles.rowCurrent);
              } else if (isHit) {
                classes.push(styles.rowHit);
              }
              return (
                <div key={index} className={classes.join(' ')}>
                  <span className={styles.gutter} style={{ width: gutterWidth }}>
                    {index + 1}
                  </span>
                  <span className={styles.content}>
                    {row.kind === 'head' ? (
                      <>
                        {`[${row.ts}] `}
                        <span className={levelClassName(row.level)}>{formatLevelCell(row.level)}</span>
                        {` [${row.instances}] ${row.text}`}
                      </>
                    ) : (
                      row.kind === 'continuation' ? `${CONTINUATION_INDENT}${row.text}` : row.text
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default StructuredLogViewer;
